import type { TemplateItem } from '@/features/templates-config';
import type { SortDirection } from '@/shared/ui/data-table';

export type EmailTemplateSortColumn = 'templateName' | 'updatedAt';

/** Epoch millis, or null when the value is absent or not a date the app can order by. */
function timeOf(updatedAt: string | undefined): number | null {
  if (!updatedAt) return null;
  const parsed = Date.parse(updatedAt);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Comparator for the templates list.
 *
 * Templates saved before `updatedAt` existed have none, and they sort **last in both directions**
 * rather than being treated as the oldest: the column renders them as an em-dash, so "unknown"
 * is what they mean, and burying them under a descending sort would read as a date we know.
 */
export function compareTemplates(column: EmailTemplateSortColumn, direction: SortDirection) {
  const orientation = direction === 'asc' ? 1 : -1;

  return (a: TemplateItem, b: TemplateItem): number => {
    if (column === 'templateName') {
      return a.templateName.localeCompare(b.templateName) * orientation;
    }

    // Unparseable is treated as unknown, matching `formatUpdatedAt`'s em-dash. A raw `NaN` here
    // makes the comparator inconsistent (it coerces to 0), which can render asc and desc alike.
    const at = timeOf(a.updatedAt);
    const bt = timeOf(b.updatedAt);

    // Unknown timestamps are pinned to the bottom, so the orientation deliberately isn't applied.
    if (at === null && bt === null) return 0;
    if (at === null) return 1;
    if (bt === null) return -1;

    return (at - bt) * orientation;
  };
}
